// Lifted from schedugram-client/react-src/components/ui/data-table/data-table-header.tsx
// — sort icons swapped from FontAwesome's fa-sort/fa-sort-up/fa-sort-down (still
// FA, just same kit) and recoloured to blurple (was schedugram-client blue).

import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { Column, SortDirection, SortState } from "./types";

interface DataTableHeaderProps<T> {
  columns: Column<T>[];
  sortState: SortState;
  onSort: (columnId: string) => void;
  stickyHeader?: boolean;
  stickyOffset?: string;
  showCellBorders?: boolean;
}

export function DataTableHeader<T>({
  columns,
  sortState,
  onSort,
  stickyHeader = true,
  stickyOffset,
  showCellBorders = false,
}: DataTableHeaderProps<T>) {
  return (
    <thead
      className={cn("bg-neutral-50", stickyHeader && "sticky z-10")}
      style={stickyHeader ? { top: stickyOffset || 0 } : undefined}
    >
      <tr>
        {columns.map((column, index) => {
          const isSorted = sortState.column === column.id;
          const sortDirection = isSorted ? sortState.direction : null;

          return (
            <th
              key={column.id}
              scope="col"
              aria-sort={
                column.sortable
                  ? sortDirection === "asc"
                    ? "ascending"
                    : sortDirection === "desc"
                      ? "descending"
                      : "none"
                  : undefined
              }
              className={cn(
                "border-b border-neutral-200 px-4 py-3 text-left",
                showCellBorders && index < columns.length - 1 && "border-r",
                column.headerClassName,
              )}
              style={{
                width: column.width,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
              }}
            >
              {column.sortable ? (
                <SortableHeader
                  column={column}
                  sortDirection={sortDirection}
                  onSort={() => onSort(column.id)}
                />
              ) : (
                <HeaderContent column={column} />
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

interface SortableHeaderProps<T> {
  column: Column<T>;
  sortDirection: SortDirection;
  onSort: () => void;
}

function SortableHeader<T>({ column, sortDirection, onSort }: SortableHeaderProps<T>) {
  const headerLabel = typeof column.header === "string" ? column.header : column.id;
  const stateLabel =
    sortDirection === "asc"
      ? "currently sorted ascending"
      : sortDirection === "desc"
        ? "currently sorted descending"
        : "not sorted";
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={onSort}
        aria-label={`Sort by ${headerLabel}, ${stateLabel}`}
        className="flex items-center gap-1.5 rounded p-0 text-sm font-semibold text-neutral-700 transition-colors hover:text-neutral-900 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
      >
        {column.header}
        <SortIcon direction={sortDirection} />
      </button>
      {column.info ? (
        <HeaderInfoTooltip label={headerLabel}>{column.info}</HeaderInfoTooltip>
      ) : null}
    </div>
  );
}

interface HeaderContentProps<T> {
  column: Column<T>;
}

function HeaderContent<T>({ column }: HeaderContentProps<T>) {
  const headerLabel = typeof column.header === "string" ? column.header : column.id;
  if (typeof column.header === "string") {
    return (
      <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-700">
        {column.header}
        {column.info ? (
          <HeaderInfoTooltip label={headerLabel}>{column.info}</HeaderInfoTooltip>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      {column.header}
      {column.info ? (
        <HeaderInfoTooltip label={headerLabel}>{column.info}</HeaderInfoTooltip>
      ) : null}
    </div>
  );
}

function HeaderInfoTooltip({
  label,
  children,
}: {
  label: string;
  children: import("react").ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`About the ${label} column`}
          className="relative inline-flex size-4 items-center justify-center text-neutral-400 transition-colors hover:text-purple-700 focus-visible:text-purple-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
        >
          <i className="fa-regular fa-circle-info text-xs" aria-hidden="true" />
          <span
            aria-hidden="true"
            className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
          />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{children}</TooltipContent>
    </Tooltip>
  );
}

interface SortIconProps {
  direction: SortDirection;
}

function SortIcon({ direction }: SortIconProps) {
  return (
    <i
      aria-hidden="true"
      className={cn(
        "fa-solid text-[10px]",
        direction === "asc" && "fa-sort-up text-purple-500",
        direction === "desc" && "fa-sort-down text-purple-500",
        !direction && "fa-sort text-neutral-400",
      )}
    />
  );
}
